import { InputV2 } from '@southfields-digital/mxmz-react-ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  useAutocompleteState,
  useFocusManagement,
  useKeyboardNavigation,
  useSmartPositioning,
} from '../hooks';
import { SuggestionOption } from '../types';

import { AutocompleteProvider, type AutocompleteContextValue } from './AutocompleteContext';
import SuggestionList from './SuggestionList';
import { AutocompleteInputProps } from './types';

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  suggestions,
  valueExtractor = (suggestion) => suggestion.label,
  placeholder = 'Type or select...',
  disabled = false,
  className = '',
  minSearchLength = 0,
  maxSuggestions = 50,
  showOnFocus = false,
  enableSpecialCharacters = true,
  onSuggestionSelect,
  onFocus,
  onBlur,
}) => {
  // Generate unique IDs for accessibility
  const inputId = useId();
  const listboxId = useId();

  // Create ref for the input element to enable precise focus detection
  const inputRef = useRef<HTMLInputElement>(null);

  // Track the last selected value to prevent immediate reopening with the same value
  const [lastSelectedValue, setLastSelectedValue] = useState<string | null>(null);

  // Enhanced autocomplete state management
  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    filteredSuggestions,
    groupedSuggestions,
    isLoading,
    error,
  } = useAutocompleteState({
    value,
    suggestions,
    minSearchLength,
    maxSuggestions,
    showOnFocus,
    inputRef,
    lastSelectedValue,
    enableSpecialCharacters,
  });

  // Focus management
  const { containerRef, handleInputFocus, handleInputBlur, cleanup } = useFocusManagement({
    onFocus,
    onBlur,
    filteredSuggestions,
    setIsOpen,
  });

  const {
    dropdownRef,
    position: dropdownPosition,
    maxHeight,
    isPositioned,
  } = useSmartPositioning({
    isOpen,
    containerRef,
  });

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Clear the last selected value when user starts typing
      setLastSelectedValue(null);

      const syntheticEvent = {
        ...e,
        currentTarget: e.target,
      } as React.SyntheticEvent<HTMLInputElement>;

      onChange(syntheticEvent);
      setHighlightedIndex(-1);
    },
    [onChange, setHighlightedIndex, setLastSelectedValue]
  );

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (suggestion: SuggestionOption) => {
      const selectedValue = valueExtractor(suggestion);

      // Store the selected value to prevent immediate reopening
      setLastSelectedValue(selectedValue);

      // Create a synthetic event with the selected value
      const syntheticEvent = {
        currentTarget: { value: selectedValue },
        target: { value: selectedValue },
      } as unknown as React.SyntheticEvent<HTMLInputElement>;

      onChange(syntheticEvent);
      onSuggestionSelect?.(suggestion);

      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [
      onChange,
      valueExtractor,
      onSuggestionSelect,
      setIsOpen,
      setHighlightedIndex,
      setLastSelectedValue,
    ]
  );

  // Enhanced keyboard navigation
  const { handleKeyDown, navigateUp, navigateDown, selectHighlighted, close } =
    useKeyboardNavigation({
      isOpen,
      filteredSuggestions,
      highlightedIndex,
      setHighlightedIndex,
      setIsOpen,
      onSuggestionSelect: handleSuggestionSelect,
    });

  // Cleanup on unmount
  useEffect(() => cleanup(), [cleanup]);

  // Create context value
  const contextValue: AutocompleteContextValue = {
    state: {
      isOpen,
      highlightedIndex,
      filteredSuggestions,
      groupedSuggestions,
      position: dropdownPosition,
      maxHeight,
      isPositioned,
      isLoading,
      error,
    },
    actions: {
      setIsOpen,
      setHighlightedIndex,
      onSuggestionSelect: handleSuggestionSelect,
      onHighlightChange: setHighlightedIndex,
      navigateUp,
      navigateDown,
      selectHighlighted,
      close,
    },
  };

  return (
    <AutocompleteProvider value={contextValue}>
      <div
        ref={containerRef}
        className={`relative w-full bg-transparent ${className}`}
        data-testid="autocomplete-input"
      >
        <InputV2
          ref={inputRef}
          id={inputId}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          autoComplete="off"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]
              ? `suggestion-${filteredSuggestions[highlightedIndex].value}`
              : undefined
          }
          aria-label={placeholder}
          data-testid="autocomplete-input-field"
        />

        <div ref={dropdownRef} id={listboxId}>
          <SuggestionList />
        </div>

        {isLoading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full" />
          </div>
        )}
      </div>
    </AutocompleteProvider>
  );
};

export default AutocompleteInput;
